import { MathUtils } from "three"

export type Snap<Section = string> = {
  snap: number
  name: Section
  height: number
}

type ScrollInfo<Section> = {
  currentSection: Snap<Section>
  nextSection?: Snap<Section>
  progress?: number
  direction?: -1 | 0 | 1
}

export class ScrollManager<Section> {
  private static instance: ScrollManager<any> | null = null // Singleton instance
  private debug: boolean

  private experience: any
  private sizes: any

  public progress: number | undefined

  private constructor({
    debug,
    experience,
  }: {
    debug?: boolean
    experience: any
  }) {
    this.debug = debug || false
    this.experience = experience

    this.sizes = this.experience.sizes

    this.raf()
  }

  static getInstance<Section extends string>(config: {
    viewportHeight: number
    experience: any
    debug?: boolean
  }): ScrollManager<Section> {
    if (!ScrollManager.instance) {
      ScrollManager.instance = new ScrollManager<Section>(config)

      if (config.debug) {
        ScrollManager.instance.showInfo()
      }
    }
    return ScrollManager.instance
  }

  /**
   * Update the progress of the scroll manager
   * I do this in a raf to avoid messed progress during fast scrolls
   */
  private raf() {
    requestAnimationFrame(() => {
      this.raf()

      const snaps = this.experience.scrollInfo.snaps
      const scroll = this.experience.scrollInfo.animatedScroll
      const direction = this.experience.scrollInfo.direction
      const viewportHeight = this.sizes.height

      if (!snaps) return

      let progress: number | undefined

      if (direction === -1) {
        for (let i = 0; i < snaps.length - 1; i++) {
          const nextSection = snaps[i]

          // Verifica se entrambe le sezioni sono visibili nel viewport
          if (
            scroll < nextSection.snap + nextSection.height &&
            scroll + viewportHeight > nextSection.snap + nextSection.height
          ) {
            progress =
              1 -
              (nextSection.snap + nextSection.height - scroll) / viewportHeight // Invertito per rappresentare il progresso nella transizione
          }
        }
      } else {
        for (let i = 1; i <= snaps.length - 1; i++) {
          const nextSection = snaps[i]

          if (
            scroll < nextSection.snap &&
            scroll + viewportHeight > nextSection.snap
          ) {
            progress =
              (scroll + viewportHeight - nextSection.snap) / viewportHeight
          }
        }
      }

      this.experience.scrollInfo.progress = progress
    })
  }

  showInfo() {
    this.debug = true
    const debugDiv = document.createElement("div")
    debugDiv.id = "scroll-manager-debug"
    debugDiv.style.position = "fixed"
    debugDiv.style.bottom = "0"
    debugDiv.style.left = "0"
    debugDiv.style.width = "300px"
    debugDiv.style.height = "130px"
    debugDiv.style.backgroundColor = "rgba(0, 0, 0, 0.5)"
    debugDiv.style.color = "white"
    debugDiv.style.padding = "10px"
    debugDiv.style.fontFamily = "monospace"
    debugDiv.style.fontSize = "16px"
    debugDiv.style.zIndex = "9999"
    document.body.appendChild(debugDiv)
  }

  removeInfo() {
    this.debug = false
    const debugDiv = document.getElementById("scroll-manager-debug")
    if (debugDiv) {
      debugDiv.remove()
    }
  }

  getScrollTransition({
    scroll: rawScroll,
    snaps,
    scrollInfo,
  }: {
    scroll: number
    snaps: Snap<Section>[]
    scrollInfo: any
  }): ScrollInfo<Section> | null {
    const prevDirection = scrollInfo.direction || 1
    const prevAnimatedScroll = scrollInfo.animatedScroll ?? rawScroll
    const viewportHeight = this.sizes.height

    const scroll = MathUtils.clamp(
      rawScroll,
      0,
      snaps[snaps.length - 1].snap + snaps[snaps.length - 1].height,
    )

    // Calculate direction based on immediate scroll change, not startingScroll
    // This fixes the issue where startingScroll is stale during rapid scroll direction changes
    const scrollDelta = scroll - prevAnimatedScroll
    const SCROLL_THRESHOLD = 0.1 // Small threshold to avoid direction changes on tiny movements

    let direction: -1 | 0 | 1
    if (Math.abs(scrollDelta) < SCROLL_THRESHOLD) {
      // No significant movement, keep previous direction
      direction = prevDirection
    } else if (scrollDelta < 0) {
      // Scrolling up
      direction = -1
    } else {
      // Scrolling down
      direction = 1
    }

    if (direction === -1) {
      // Scroll verso l'alto
      for (let i = 0; i < snaps.length - 1; i++) {
        const nextSection = snaps[i]
        const currentSection = snaps[i + 1]

        const nextSectionBottom = nextSection.snap + nextSection.height

        // Verifica se entrambe le sezioni sono visibili nel viewport
        if (
          scroll < nextSectionBottom &&
          scroll + viewportHeight > nextSectionBottom
        ) {
          const progress =
            1 -
            (nextSection.snap + nextSection.height - scroll) / viewportHeight

          if (nextSection.name === "guestbook") {
            console.log("-> This might be the issue")
          }

          const scrollInfo = {
            currentSection,
            nextSection,
            progress, // Invertito per rappresentare il progresso nella transizione
            direction,
          }

          this.logDebug(scrollInfo)

          return scrollInfo
        }
      }
    } else if (direction === 1) {
      // Scroll verso il basso
      for (let i = 1; i <= snaps.length - 1; i++) {
        const nextSection = snaps[i]
        const currentSection = snaps[i - 1]

        if (
          scroll < nextSection.snap &&
          scroll + viewportHeight > nextSection.snap
        ) {
          const progress =
            (scroll + viewportHeight - nextSection.snap) / viewportHeight

          const scrollInfo = {
            currentSection,
            nextSection,
            progress,
            direction,
          }

          this.logDebug(scrollInfo)

          return scrollInfo
        }
      }

      // Special case: only when we're actually at maximum scroll position
      // This ensures the last section is shown at the end of the page
      const lastSection = snaps[snaps.length - 1]
      const maxScroll = lastSection.snap + lastSection.height - viewportHeight
      const isAtMaxScroll = scroll >= maxScroll - 1 // Small tolerance for floating point

      if (isAtMaxScroll) {
        // At maximum scroll, we're fully in the last section
        const scrollInfo = {
          currentSection: lastSection,
          nextSection: undefined,
          progress: undefined,
          direction,
        }

        this.logDebug(scrollInfo)
        return scrollInfo
      }
    }

    // Define currentSection - find the section that contains the current scroll position
    // Iterate forward to find the first section where scroll is within its bounds
    let currentSection = snaps[0]

    for (let i = 0; i < snaps.length; i++) {
      if (scroll < snaps[i].snap + snaps[i].height) {
        currentSection = snaps[i]
        break
      }
    }

    const newScrollInfo = {
      direction,
      currentSection,
      nextSection: undefined,
      progress: undefined,
    }

    this.logDebug(newScrollInfo)

    // Nessuna transizione in corso
    return newScrollInfo
  }

  getCurrentSection({
    scroll,
    snaps,
  }: {
    scroll: number
    snaps: Snap<Section>[]
  }): Snap<Section> | null {
    const viewportHeight = this.sizes.height
    const scrollPosition = scroll + viewportHeight / 2 // Calcola la metà del viewport

    for (let i = snaps.length - 1; i >= 0; i--) {
      if (scrollPosition >= snaps[i].snap) {
        return snaps[i] // Restituisce la prima sezione che contiene la posizione corrente
      }
    }

    return null // Nessuna sezione trovata
  }

  logDebug(data: ScrollInfo<Section> | null): void {
    if (this.debug) {
      const debugDiv = document.getElementById("scroll-manager-debug")

      const nextSectionName = data?.nextSection?.name || "-"
      const currentSectionName = data?.currentSection?.name || "-"
      const progress = data?.progress?.toFixed(4) || "-"
      const direction = data
        ? data.direction === 1
          ? "down"
          : data.direction === -1
            ? "up"
            : "0"
        : "-"

      if (debugDiv) {
        debugDiv.innerHTML = `
        Current Section:
          ${currentSectionName}
          <br>
          Next Section:
          ${nextSectionName}
          <br>
          Progress: ${progress} (default: 0)
          <br>
          Direction: ${direction}
        `
      }
    }
  }
}
